import { useState } from "react";
import axios from "axios";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { toast } from "sonner";
import { Check, Pencil, Trash2, Ban, AlertTriangle, X } from "lucide-react";
import { AI2 } from "./api";

const STATUS = { active: ["Live", "var(--accent-ink)"], pending: ["Waiting for approval", "#B7791F"], disabled: ["Disabled", "var(--muted)"] };
const when = (d) => (d ? new Date(d).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }) : "—");

export const LearnedItem = ({ item, kind, onChanged, byId }) => {
  const field = kind === "rules" ? "rule" : "answer";
  const [edit, setEdit] = useState(false);
  const [val, setVal] = useState(item[field]);
  const base = `${AI2}/learning/${kind}/${item.id}`;
  const act = (p) => p.then(() => onChanged()).catch((e) => toast.error(e.response?.data?.detail || "Couldn't update"));
  const patch = (body) => act(axios.patch(base, body));
  const [label, color] = STATUS[item.status] || STATUS.active;
  const conflicts = item.conflicts_with || [];
  return (
    <div className="card" style={{ padding: 14, borderLeft: conflicts.length ? "4px solid #E5484D" : undefined }} data-testid={`ai2-learned-${item.id}`}>
      <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", fontSize: 12 }}>
        <b style={{ color }} data-testid={`ai2-learned-status-${item.id}`}>{label}</b>
        <span className="muted">· {item.scope === "all" ? "All locations" : item.location_name} · by {item.created_by_name} ({item.created_by_role}) · {when(item.created_at)}</span>
        {kind === "rules" && <span className="mono muted">· used {item.uses || 0}×{item.last_used_at ? `, last ${when(item.last_used_at)}` : ""}</span>}
      </div>
      <div className="muted" style={{ fontSize: 12, margin: "6px 0" }}>Question: {item.question}</div>
      {edit ? (
        <textarea className="input" rows={kind === "rules" ? 3 : 8} style={{ width: "100%" }} value={val} onChange={(e) => setVal(e.target.value)} data-testid={`ai2-learned-edit-${item.id}`} />
      ) : kind === "rules" ? (
        <div style={{ fontSize: 14, whiteSpace: "pre-wrap" }}>{item[field]}</div>
      ) : (
        <div className="ai2-md" style={{ maxHeight: 220, overflow: "auto" }}><ReactMarkdown remarkPlugins={[remarkGfm]}>{item[field]}</ReactMarkdown></div>
      )}
      {conflicts.length > 0 && (
        <div className="alert" style={{ marginTop: 10, fontSize: 12 }} data-testid={`ai2-conflict-${item.id}`}>
          <AlertTriangle size={14} color="#E5484D" />
          <div>Conflicts with: {conflicts.map((c) => `"${byId[c]?.rule || "another correction"}"`).join("; ")}
            <div style={{ marginTop: 6 }}><button className="btn btn-sm" onClick={() => act(axios.post(`${base}/resolve`))} data-testid={`ai2-resolve-${item.id}`}><Check size={12} />Keep this one</button></div>
          </div>
        </div>
      )}
      <div style={{ display: "flex", gap: 6, marginTop: 10, flexWrap: "wrap" }}>
        {edit ? (
          <>
            <button className="btn btn-sm btn-primary" onClick={() => { patch({ [field]: val }); setEdit(false); }} data-testid={`ai2-learned-save-${item.id}`}><Check size={12} />Save</button>
            <button className="btn btn-sm" onClick={() => { setVal(item[field]); setEdit(false); }}><X size={12} />Cancel</button>
          </>
        ) : <button className="btn btn-sm" onClick={() => setEdit(true)} data-testid={`ai2-learned-editbtn-${item.id}`}><Pencil size={12} />Edit</button>}
        {item.status !== "active" && <button className="btn btn-sm btn-primary" onClick={() => patch({ status: "active" })} data-testid={`ai2-learned-approve-${item.id}`}><Check size={12} />{item.status === "pending" ? "Approve" : "Enable"}</button>}
        {item.status === "active" && <button className="btn btn-sm" onClick={() => patch({ status: "disabled" })} data-testid={`ai2-learned-disable-${item.id}`}><Ban size={12} />Disable</button>}
        {kind === "rules" && <button className="btn btn-sm" onClick={() => patch({ scope: item.scope === "all" ? "location" : "all" })} data-testid={`ai2-learned-scope-${item.id}`}>{item.scope === "all" ? "Limit to this location" : "Apply to all locations"}</button>}
        <button className="btn btn-sm btn-ghost" onClick={() => window.confirm("Delete this permanently?") && act(axios.delete(base))} data-testid={`ai2-learned-delete-${item.id}`}><Trash2 size={12} />Delete</button>
      </div>
    </div>
  );
};
